import {
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useRef,
} from 'react';

const MOVE_TOLERANCE = 10;

/**
 * Calls `onLongPress` after a `delay`ms primary-button press and swallows the
 * click that ends it. Pass `undefined` to disable.
 *
 * Cancellation listens on `window`, not the element: a dropdown opened by
 * the press can cover the trigger, so the element may never see `pointerup`.
 */
export function useLongPress(onLongPress?: () => void, delay = 500) {
  const fired = useRef(false);
  const cancel = useRef<() => void>(undefined);

  useEffect(() => () => cancel.current?.(), []);

  const onPointerDown = useCallback(
    (e: ReactPointerEvent) => {
      fired.current = false;
      cancel.current?.();
      if (!onLongPress || e.button !== 0) return;

      const { clientX, clientY } = e;
      let timer = 0;
      const stop = () => {
        window.clearTimeout(timer);
        window.removeEventListener('pointerup', stop);
        window.removeEventListener('pointercancel', stop);
        window.removeEventListener('pointermove', onMove);
        cancel.current = undefined;
      };
      const onMove = (ev: PointerEvent) => {
        if (
          Math.hypot(ev.clientX - clientX, ev.clientY - clientY) >
          MOVE_TOLERANCE
        ) {
          stop();
        }
      };
      timer = window.setTimeout(() => {
        stop();
        fired.current = true;
        onLongPress();
      }, delay);
      window.addEventListener('pointerup', stop);
      window.addEventListener('pointercancel', stop);
      window.addEventListener('pointermove', onMove);
      cancel.current = stop;
    },
    [delay, onLongPress]
  );

  const onClickCapture = useCallback((e: ReactMouseEvent) => {
    if (!fired.current) return;
    fired.current = false;
    e.stopPropagation();
    e.preventDefault();
  }, []);

  // keeps Android's long-press context menu / text selection out of the way
  const onContextMenu = useCallback(
    (e: ReactMouseEvent) => {
      if (onLongPress) e.preventDefault();
    },
    [onLongPress]
  );

  return { onPointerDown, onClickCapture, onContextMenu };
}
